import React, { useEffect, useMemo, useState } from 'react'
import styled from '@emotion/styled'
import tw from 'twin.macro'
import { findIndex } from 'lodash-es'
import { Popover, Checkbox, Global } from '@mantine/core'
import clsx from 'clsx'
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'
import { Button, Input } from '@ow/comps'
import { useTheme } from '@ow/theme'
import { useAppEx, useProjectStore, useTbViewStore } from '@vt/hooks'

const Item = styled.div`
  ${tw`focus:outline-none`}
`

const forbidSlugs = ['standardName']

/**
 * 配置显隐列
 * @param {*} columns 所有可操作的列
 * @param {*} columnShowType 列显隐信息
 * @param {*} onChangeColumnShowType 回调方法
 * @returns
 */

const ColumnConfig = function () {
  const { themeClass } = useTheme()
  const [filterColumns, setFilterColumns] = useState([])
  const [currentColumns, setCurrentColumns] = useState([])
  const [open, setOpen] = useState(false)
  const { getVtProject } = useProjectStore()
  const { appId, userInfoInApp } = useAppEx()
  const vtProject = getVtProject()
  const {
    tbSlug,
    id: vid,
    fields,
    matrixColumns,
    columnShowType,
    setColumnShowType,
  } = useTbViewStore([
    'tbSlug',
    'id',
    'fields',
    'columnShowType',
    'matrixColumns',
    'setColumnShowType',
  ])

  const finalFields = matrixColumns || fields
  const onDragEnd = (result) => {
    if (!result.destination) {
      return
    }
    const arr = [...currentColumns]
    const data = arr[result.source.index]
    arr.splice(result.source.index, 1)
    arr.splice(result.destination.index, 0, data)
    setCurrentColumns(arr)
  }

  const onChangeColumns = (slug, isSelected) => {
    const arr = [...currentColumns]
    if (slug === 'all') {
      setCurrentColumns(
        arr.map((v) => {
          if (v.showType !== 'sticky') {
            return { ...v, showType: isSelected ? 'show' : 'hidden' }
          }
          return v
        })
      )
    } else {
      const num = findIndex(currentColumns, (v) => v.slug === slug)
      const obj = currentColumns.find((v) => v.slug === slug)
      arr.splice(num, 1, {
        slug,
        ...obj,
        showType: isSelected ? 'show' : 'hidden',
      })
      setCurrentColumns(arr)
    }
  }

  const onConfirm = () => {
    setColumnShowType(currentColumns)
    setOpen(!open)
    setFilterColumns([])

    vtProject.setStorage({
      key: `${appId}_${tbSlug}_${vid}_${userInfoInApp.userId}_listConfig`,
      data: currentColumns,
    })

    // 存到storage
  }

  const isShowAll = useMemo(() => {
    const arr = [...currentColumns]
    const obj = arr.find((v) => v.showType === 'hidden')
    if (obj) {
      return false
    }
    return true
  }, [currentColumns])

  useEffect(() => {
    setCurrentColumns(
      columnShowType.filter((v) => !forbidSlugs.includes(v.slug))
    )
  }, [columnShowType])

  return (
    <div>
      <Global
        styles={() => ({
          '.dark': {
            '.mantine-Popover-dropdown': {
              background: 'var(--ow-color-darkTheme-1100)',
              border: '1px solid var(--ow-color-darkTheme-800)',
              color: '#fff',
            },

            '.mantine-TextInput-input': {
              background: 'var(--ow-color-darkTheme-1100)',
              border: '1px solid var(--ow-color-darkTheme-800)',
              color: '#fff',
            },
          },
        })}
      />

      <Popover
        opened={open}
        onClose={() => {}}
        width={340}
        position="bottom"
        styles={{
          wrapper: { marginRight: '20px' },
          body: {
            border: 'none',
            boxShadow:
              '0 5px 5px -3px rgba(0,0,0,0.10), 0 8px 10px 1px rgba(0,0,0,0.06), 0 3px 14px 2px rgba(0,0,0,0.05)',
          },
        }}
      >
        <Popover.Target>
          <div
            className="cursor-pointer w-8 h-8 flex items-center justify-center dark:hover:bg-darkTheme-900"
            style={{ borderRadius: '4px' }}
            onClick={() => {
              setOpen(!open)
            }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="icon icon-tabler icon-tabler-settings"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              strokeWidth="2"
              stroke="currentColor"
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path stroke="none" d="M0 0h24v24H0z" fill="none" />
              <path d="M10.325 4.317c.426 -1.756 2.924 -1.756 3.35 0a1.724 1.724 0 0 0 2.573 1.066c1.543 -.94 3.31 .826 2.37 2.37a1.724 1.724 0 0 0 1.065 2.572c1.756 .426 1.756 2.924 0 3.35a1.724 1.724 0 0 0 -1.066 2.573c.94 1.543 -.826 3.31 -2.37 2.37a1.724 1.724 0 0 0 -2.572 1.065c-.426 1.756 -2.924 1.756 -3.35 0a1.724 1.724 0 0 0 -2.573 -1.066c-1.543 .94 -3.31 -.826 -2.37 -2.37a1.724 1.724 0 0 0 -1.065 -2.572c-1.756 -.426 -1.756 -2.924 0 -3.35a1.724 1.724 0 0 0 1.066 -2.573c-.94 -1.543 .826 -3.31 2.37 -2.37c1 .608 2.296 .07 2.572 -1.065z" />
              <circle cx="12" cy="12" r="3" />
            </svg>
          </div>
        </Popover.Target>
        <Popover.Dropdown>
          <Input
            autoComplete="off"
            style={{
              paddingLeft: '0.75rem',
              paddingRight: '0.75rem',
              fontSize: '0.8125rem',
              width: '100%',
            }}
            onChange={(e) => {
              const { value } = e.target
              const slugs = currentColumns.map((v) => v.slug)
              const arrColumns = finalFields.filter((v) =>
                slugs.includes(v.slug)
              )

              const arr = value
                ? arrColumns.filter((v) => (v.title || '').indexOf(value) > -1)
                : []

              setFilterColumns(arr.filter((v) => v.showType !== 'sticky'))
            }}
          />

          {filterColumns.length ? (
            <div
              className={clsx(
                themeClass,
                'w-full overflow-y-auto h-72 text-gray-100 dark:text-white'
              )}
              style={{
                fontSize: '14px',
              }}
            >
              {filterColumns.map((item) => {
                const { title, slug } = item
                const isSelected = columnShowType.find(
                  (o) => o.slug === slug && o.showType === 'show'
                )

                return (
                  <Item
                    className="w-full flex items-center cursor-pointer draggable h-10 gap-3"
                    key={slug}
                  >
                    <Checkbox
                      defaultChecked={isSelected}
                      size="xs"
                      onChange={(e) =>
                        onChangeColumns(slug, e.currentTarget.checked)
                      }
                    />
                    {title}
                  </Item>
                )
              })}
            </div>
          ) : (
            <>
              <Item
                className="w-full flex items-center cursor-pointer draggable h-10 gap-3"
                style={{
                  fontSize: '14px',
                }}
              >
                <Checkbox
                  defaultChecked={isShowAll}
                  size="xs"
                  onChange={(e) =>
                    onChangeColumns('all', e.currentTarget.checked)
                  }
                />
                全选
              </Item>
              <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="columns-droppable">
                  {(dropableProvided) => (
                    <div
                      {...dropableProvided.droppableProps}
                      ref={dropableProvided.innerRef}
                      className={clsx(
                        themeClass,
                        'w-full overflow-y-auto h-72 text-gray-100 dark:text-white'
                      )}
                      style={{
                        fontSize: '14px',
                        // color: 'var(--ow-color-gray-100)',
                      }}
                    >
                      {currentColumns
                        .filter((v) => v.showType !== 'sticky')
                        .map((item, index) => {
                          const field =
                            finalFields.find((v) => v.slug === item.slug) || {}
                          const { slug, title } = field
                          const isSelected =
                            item.showType === 'show' || !item.showType

                          const defaultSelected = !!columnShowType.find(
                            (o) => o.slug === slug && o.showType === 'show'
                          )

                          const props = {}
                          if (isSelected) {
                            props.checked = isSelected
                          } else {
                            props.checked = false
                          }

                          return (
                            <Draggable
                              key={slug}
                              draggableId={slug}
                              index={index}
                            >
                              {(providedItem, snapshotItem) => (
                                <Item
                                  className="w-full flex items-center cursor-pointer draggable h-10 gap-3"
                                  key={slug}
                                  ref={providedItem.innerRef}
                                  {...providedItem.draggableProps}
                                  {...providedItem.dragHandleProps}
                                  isDragging={snapshotItem.isDragging}
                                >
                                  <Checkbox
                                    defaultChecked={defaultSelected}
                                    size="xs"
                                    onChange={(e) => {
                                      onChangeColumns(
                                        slug,
                                        e.currentTarget.checked
                                      )
                                    }}
                                    {...props}
                                  />
                                  {title}
                                </Item>
                              )}
                            </Draggable>
                          )
                        })}
                      {dropableProvided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            </>
          )}
          <div
            className={clsx(
              themeClass,
              'w-full flex items-center justify-center gap-5 border-t dark:border-darkTheme-800 border-solid h-12 mt-4'
            )}
          >
            <Button
              stroke
              onClick={() => {
                setOpen(!open)
                setFilterColumns([])
              }}
              className="dark:bg-darkTheme-800 dark:border-darkTheme-1100 dark:text-dark-200"
            >
              取消
            </Button>
            <Button type="primary" onClick={onConfirm}>
              确定
            </Button>
          </div>
        </Popover.Dropdown>
      </Popover>
    </div>
  )
}

export default ColumnConfig
